import React, { createContext, useReducer, useContext, ReactNode } from 'react';
import { DataState, DataSetAction } from './dataTypes';
const initialState: DataState = {
    pointsMetadata: {}
};

const DataContext = createContext<{ state: DataState; dispatch: React.Dispatch<DataSetAction> } | undefined>(undefined);
const dataReducer = (state: DataState, action: DataSetAction): DataState => {
    switch (action.type) {
        case 'SET_LABEL':
            return {
                ...state,
                pointsMetadata: {
                    ...state.pointsMetadata,
                    [action.payload.pointId]: {
                        label: action.payload.label,
                        datasets: state.pointsMetadata[action.payload.pointId]?.datasets || {}
                    },
                },
            };
        case 'SET_TEMP_DATASET':
            return {
                ...state,
                pointsMetadata: {
                    ...state.pointsMetadata,
                    [action.payload.pointId]: {
                        label: action.payload.label,
                        datasets: {
                            ...(state.pointsMetadata[action.payload.pointId]?.datasets || {}),
                            [action.payload.datasetId]: {
                                ...state.pointsMetadata[action.payload.pointId]?.datasets[action.payload.datasetId],
                                tempDatasetName: action.payload.name
                            }
                        }
                    }
                }
            };
        case 'SET_DATASET':
            return {
                ...state,
                pointsMetadata: {
                    ...state.pointsMetadata,
                    [action.payload.pointId]: {
                        label: action.payload.label,
                        datasets: {
                            ...(state.pointsMetadata[action.payload.pointId]?.datasets || {}),
                            [action.payload.datasetId]: {
                                ...state.pointsMetadata[action.payload.pointId]?.datasets[action.payload.datasetId],
                                metadata: action.payload.metadata
                            }
                        }
                    }
                }
            };
        case 'SET_DATASET_IS_OFFLINE':
            return {
                ...state,
                pointsMetadata: {
                    ...state.pointsMetadata,
                    [action.payload.pointId]: {
                        label: action.payload.label,
                        datasets: {
                            ...(state.pointsMetadata[action.payload.pointId]?.datasets || {}),
                            [action.payload.datasetId]: {
                                ...state.pointsMetadata[action.payload.pointId]?.datasets[action.payload.datasetId],
                                isOffline: action.payload.isOffline
                            }
                        }
                    }
                }
            };
        case 'SET_PRICE':
            return {
                ...state,
                pointsMetadata: {
                    ...state.pointsMetadata,
                    [action.payload.pointId]: {
                        label: action.payload.label,
                        datasets: {
                            ...(state.pointsMetadata[action.payload.pointId]?.datasets || {}),
                            [action.payload.datasetId]: {
                                ...state.pointsMetadata[action.payload.pointId]?.datasets[action.payload.datasetId],
                                price: action.payload.price
                            }
                        }
                    }
                }
            };
        case 'SET_BEST_DATASET':
            return {
                ...state,
                pointsMetadata: {
                    ...state.pointsMetadata,
                    [action.payload.pointId]: {
                        label: action.payload.label,
                        datasets: {
                            ...(state.pointsMetadata[action.payload.pointId]?.datasets || {}),
                            [action.payload.datasetId]: {
                                ...state.pointsMetadata[action.payload.pointId]?.datasets[action.payload.datasetId],
                                bestDataset: action.payload.bestDataset
                            }
                        }
                    }
                }
            };
        case 'SET_HAS_REPORTS':
            return {
                ...state,
                pointsMetadata: {
                    ...state.pointsMetadata,
                    [action.payload.pointId]: {
                        label: action.payload.label,
                        datasets: {
                            ...(state.pointsMetadata[action.payload.pointId]?.datasets || {}),
                            [action.payload.datasetId]: {
                                ...state.pointsMetadata[action.payload.pointId]?.datasets[action.payload.datasetId],
                                hasReports: action.payload.hasReports
                            }
                        }
                    }
                }
            };
        case 'SET_USER_GROUP_PERMISSIONS':
            return {
                ...state,
                pointsMetadata: {
                    ...state.pointsMetadata,
                    [action.payload.pointId]: {
                        label: action.payload.label,
                        datasets: {
                            ...(state.pointsMetadata[action.payload.pointId]?.datasets || {}),
                            [action.payload.datasetId]: {
                                ...state.pointsMetadata[action.payload.pointId]?.datasets[action.payload.datasetId],
                                userGroupPermissions: action.payload.userGroupPermissions
                            }
                        }
                    }
                }
            };
        case 'SET_CELL_SIZE':
            return {
                ...state,
                pointsMetadata: {
                    ...state.pointsMetadata,
                    [action.payload.pointId]: {
                        label: action.payload.label,
                        datasets: {
                            ...(state.pointsMetadata[action.payload.pointId]?.datasets || {}),
                            [action.payload.datasetId]: {
                                ...state.pointsMetadata[action.payload.pointId]?.datasets[action.payload.datasetId],
                                cellSize: action.payload.cellSize
                            }
                        }
                    }
                }
            }
        case 'SET_DEPTH':
            return {
                ...state,
                pointsMetadata: {
                    ...state.pointsMetadata,
                    [action.payload.pointId]: {
                        label: action.payload.label,
                        datasets: {
                            ...(state.pointsMetadata[action.payload.pointId]?.datasets || {}),
                            [action.payload.datasetId]: {
                                ...state.pointsMetadata[action.payload.pointId]?.datasets[action.payload.datasetId],
                                depth: action.payload.depth
                            }
                        }
                    }
                }
            };
        case 'SET_RADIO_KEY':
            return {
                ...state,
                pointsMetadata: {
                    ...state.pointsMetadata,
                    [action.payload.pointId]: {
                        label: action.payload.label,
                        datasets: {
                            ...(state.pointsMetadata[action.payload.pointId]?.datasets || {}),
                            [action.payload.datasetId]: {
                                ...state.pointsMetadata[action.payload.pointId]?.datasets[action.payload.datasetId],
                                radioKey: action.payload.radioKey
                            }
                        }
                    }
                }
            };
        case 'DELETE_GROUP': {
            const newState = { ...state };
            delete newState.pointsMetadata[action.payload.pointId];
            return newState;
        }
        case 'REPLACE_DATASET': {
            const {
                pointId,
                datasetId,
                metadata,
                userGroupPermissions,
                hasReports,
                isOffline,
                cellSize,
                depth
            } = action.payload;

            if (!state.pointsMetadata[pointId]) {
                return state;
            }

            const updatedDataset = {
                metadata,
                ...(userGroupPermissions ? { userGroupPermissions } : {}),
                ...(typeof hasReports !== 'undefined' ? { hasReports } : {}),
                ...(typeof isOffline !== 'undefined' ? { isOffline } : {}),
                ...(typeof depth !== 'undefined' ? { depth } : {}),
                ...(typeof cellSize !== 'undefined' ? { cellSize } : {}),
            };

            const updatedGroup = {
                ...state.pointsMetadata[pointId],
                datasets: {
                    ...state.pointsMetadata[pointId].datasets,
                    [datasetId]: updatedDataset
                }
            };

            return {
                ...state,
                pointsMetadata: {
                    ...state.pointsMetadata,
                    [pointId]: updatedGroup
                }
            };
        }
        case 'REPLACE_DATASET_ID': {
            const { pointId, oldId, newId } = action.payload;

            if (!state.pointsMetadata[pointId] || !state.pointsMetadata[pointId].datasets[oldId]) {
                return state;
            }

            const datasetToMove = state.pointsMetadata[pointId].datasets[oldId];

            const updatedDatasets = { ...state.pointsMetadata[pointId].datasets };

            delete updatedDatasets[oldId];
            updatedDatasets[newId] = datasetToMove;

            return {
                ...state,
                pointsMetadata: {
                    ...state.pointsMetadata,
                    [pointId]: {
                        ...state.pointsMetadata[pointId],
                        datasets: updatedDatasets
                    }
                }
            };
        }
        case 'SET_DATE_RANGE': {
            const { pointId, datasetId, startDate, endDate } = action.payload;
            return {
                ...state,
                pointsMetadata: {
                    ...state.pointsMetadata,
                    [pointId]: {
                        ...state.pointsMetadata[pointId],
                        datasets: {
                            ...state.pointsMetadata[pointId].datasets,
                            [datasetId]: {
                                ...state.pointsMetadata[pointId].datasets[datasetId],
                                startDate,
                                endDate,
                            },
                        },
                    },
                },
            };
        }
        case 'RESET_STATE':
            return { pointsMetadata: {} };
        default:
            return state;
    }
};

type DataProviderProps = {
    children: ReactNode;
};

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
    const [state, dispatch] = useReducer(dataReducer, initialState);

    return <DataContext.Provider value={{ state, dispatch }}>{children}</DataContext.Provider>;
};

export const useData = () => {
    const context = useContext(DataContext);
    if (context === undefined) {
        throw new Error('useData must be used within a DataProvider');
    }
    return context;
};
